/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Errors } from 'react-redux-form';
import { formatText } from 'core/i18n';
import { recordError } from 'utilities/uTagging';
import { getReferencePrefixByCountry } from 'utilities/multiLanguageHelper';
import scroll from 'utilities/scroll';
import classNames from 'classnames';
import { PrefixSelector } from '../prefixSelector/prefixSelector';
import StringHelper from 'utilities/stringHelper';
import LoadingIcon from 'assets/icons/otp-loading.svg';
import { isChrome, isMobile } from 'utilities/browser';
import { useDebounce } from 'utilities/hooks/useDebounce';
import './inputText.scss';

export const InputText = ({
    userProfileForm,
    profileModel,
    onChange,
    validations,
    type = 'text',
    inputMode,
    pattern,
    maxLength = '',
    onBlur = null,
    formActions,
    displayErrors = true,
    replaceFunction,
    onFocus = null,
    showErrorOnFocus,
    focusAnimated = false,
    noFocusFromLabel,
    autoCompleteField = true,
    autoCompletePassword,
    autoCompleteFieldCustom = 'off',
    errorClass = 'form__field_error',
    userProfile,
    uppercaseText = false,
    defaultValue,
    messagesError = {},
    style = {},
    label = '',
    inputClass = '',
    minLength = '',
    min = '',
    max = '',
    disabled = false,
    onKeyDown = null,
    onKeyUp = null,
    inputIndex = null,
    pasteDisabled,
    onPaste = null,
    tooltip,
    id = null,
    areaCode = false,
    value,
    readOnly = false,
    placeholder = '',
    handleOnClick = () => {},
    adornment,
    prefixSelection,
    handleSave,
    isValidated,
    isNotValidated,
    onlyDigits,
    isPassword = false,
    validateOnChange,
    debounceValidationTime,
    innerRef,
    isLoading,
    eventCategoryTag,
    rightSideContent,
}) => {
    const [trigger, setTrigger] = useState(false);
    const [classFocus, setClassFocus] = useState(false);
    const [classError, setClassError] = useState(false);
    let divisaderoError = '';

    useMemo(() => {
        const modelProp = profileModel?.split('.')[1];
        if (userProfileForm?.[modelProp]?.valid !== undefined && !userProfileForm?.[modelProp]?.valid !== classError) {
            setClassError(!userProfileForm?.[modelProp]?.valid);
        }
    }, [userProfileForm, profileModel]);

    useEffect(() => {
        if (!trigger) return;
        const modelProp = profileModel?.split('.')[1];
        if (userProfileForm?.[modelProp]?.valid) {
            isValidated?.();
        } else {
            if (divisaderoError) {
                recordError(
                    `${modelProp}:${divisaderoError}`,
                    userProfileForm.investmentType?.value || '',
                    eventCategoryTag
                );
            }
            setClassError(displayErrors);
            isNotValidated?.();
        }

        setTrigger(false);
    }, [trigger]);

    const handleMaxLength = (event, length) => {
        const val = event.target.value;

        if (val.length > length) {
            event.target.value = val.substring(0, length);
        }
    };

    const executeValidations = target => {
        if (validations !== null && typeof validations !== 'undefined') {
            const newValidations = Object.entries(validations).reduce((acc, [key, value]) => {
                if (key === 'required') {
                    return { ...acc, [key]: value };
                }

                if (target.value !== '') {
                    return { ...acc, [key]: value };
                }

                return { ...acc };
            }, {});

            formActions.validate(profileModel, newValidations);
        }
    };

    const onValidations = (value, target) => {
        let valueToSave = value.trim();
        if (replaceFunction) {
            valueToSave = replaceFunction(valueToSave);
            target.value = valueToSave;
        }
        formActions.change(profileModel, valueToSave);
        executeValidations(target);
        setTrigger(true);
        setClassFocus(false);
    };

    const resetStates = () => {
        setTrigger(false);
        setClassFocus(false);
        setClassError(false);
    };

    const handleChange = event => {
        if (uppercaseText) {
            const valueUppercase = event.target.value.toUpperCase();
            event.target.value = valueUppercase;
        }

        if (onlyDigits) {
            event.target.value = StringHelper.getDigitsOnly(event.target.value);
            handleMaxLength(event, maxLength);
        }

        onChange?.(event);

        if (isPassword) {
            resetStates();
        }

        if (type === 'number') {
            handleMaxLength(event, maxLength);
        }

        if (onChange || validateOnChange) {
            onValidations(event.target.value, event.target);
        }
    };

    const debouncedOnChange = useDebounce(handleChange, debounceValidationTime);

    const handleDebounceOnChange = event => {
        if (!debounceValidationTime) return handleChange(event);
        event.persist();
        resetStates();
        debouncedOnChange(event);
    };

    const handleOnBlur = event => {
        const trimmedValue = event.target.value.trim();
        const newValue = onBlur?.(event.target.value) ?? null;
        const updatedValue = newValue ?? trimmedValue;

        if (newValue !== null) {
            event.target.value = newValue;
        }

        onValidations(updatedValue, event.target);
    };

    const onFocusAnimate = () => {
        const parentElement = document.getElementById(`id_wrapper_${profileModel.split('.')[1]}`);
        if (parentElement) {
            scroll.transition(parentElement);
        }
    };

    const handleOnFocus = event => {
        !showErrorOnFocus && setClassError(false);

        if (onFocus !== null) {
            onFocus(event);
        }

        setClassFocus(true);
        if (focusAnimated) {
            onFocusAnimate();
        }
    };

    const handleOnClickLabel = () => {
        if (noFocusFromLabel) {
            return;
        }
        const modelProp = profileModel.split('.')[1];
        document.getElementById(modelProp).focus();
    };

    const renderErrors = propsFromChilds => {
        divisaderoError = propsFromChilds.children;
        if (displayErrors && classError) {
            return <p className={errorClass}>{propsFromChilds.children}</p>;
        }

        return null;
    };

    const typeAutoComplete = () => {
        if (!autoCompleteField) {
            return isChrome() ? 'none' : 'off';
        }
        if (autoCompletePassword === false) {
            return 'new-password';
        }

        return autoCompleteFieldCustom;
    };

    const isGroup = areaCode || prefixSelection ? 'input-group' : '';
    const isRightSideContent = rightSideContent ? 'right-side-content' : '';
    const showAreaCode = areaCode ? 'input-group-addon visible' : 'input-group-addon hidden';
    const modelProp = profileModel.split('.')[1];
    const messageError = Object.keys(messagesError).length
        ? messagesError
        : {
              required: formatText('validation-fieldRequired'),
              valid: formatText('validation-text'),
          };
    const className = classNames(
        'form__input',
        { 'form__input--focus': classFocus },
        { 'form__input--error valid-error': classError }
    );
    const getInputValue = () => {
        if (value) return;

        return defaultValue || userProfile?.[modelProp] || '';
    };

    if (pasteDisabled && document.getElementById(modelProp)) {
        const field = document.getElementById(modelProp);
        field.onpaste = function (e) {
            e.preventDefault();
        };
    }

    return (
        <div id={`id_wrapper_${modelProp}`}>
            <div className={className} style={style}>
                {label && (
                    <>
                        <label htmlFor={id} onClick={handleOnClickLabel}>
                            {label}
                        </label>
                        {tooltip}
                    </>
                )}
                <div className={`input-text-container ${isGroup} ${isRightSideContent}`}>
                    {prefixSelection ? (
                        <PrefixSelector
                            handleSave={handleSave}
                            userProfile={userProfile}
                            executeValidations={executeValidations}
                            inputValue={getInputValue()}
                        />
                    ) : (
                        areaCode && <span className={showAreaCode}>{getReferencePrefixByCountry()}</span>
                    )}
                    <input
                        ref={innerRef}
                        type={type}
                        id={id || modelProp}
                        className={inputClass + (innerRef?.value?.length > 0 ? ' has-value ' : '')}
                        name={modelProp}
                        inputMode={inputMode}
                        pattern={pattern}
                        defaultValue={getInputValue()}
                        placeholder={placeholder}
                        onChange={handleDebounceOnChange}
                        onBlur={handleOnBlur}
                        onFocus={handleOnFocus}
                        minLength={minLength}
                        maxLength={maxLength}
                        min={min}
                        max={max}
                        data-inputindex={inputIndex}
                        disabled={disabled}
                        onKeyDown={onKeyDown}
                        onKeyUp={onKeyUp}
                        autoComplete={typeAutoComplete()}
                        onPaste={onPaste}
                        value={value}
                        readOnly={readOnly}
                        onClick={handleOnClick}
                    />
                    {rightSideContent && !isMobile() && rightSideContent}
                    {isLoading && <img className="loading-input" src={LoadingIcon} alt={'loading-icon'} />}
                </div>
                {adornment}
            </div>
            <Errors model={profileModel} messages={messageError} component={renderErrors} />
            {rightSideContent && isMobile() && rightSideContent}
        </div>
    );
};

InputText.propTypes = {
    inputIndex: PropTypes.number,
    focusAnimated: PropTypes.bool,
    areaCode: PropTypes.bool,
    readOnly: PropTypes.bool,
    autoCompleteFieldCustom: PropTypes.string,
    adornment: PropTypes.any,
    replaceFunction: PropTypes.func,
    userProfileForm: PropTypes.object,
    profileModel: PropTypes.string,
    onChange: PropTypes.func,
    validations: PropTypes.object,
    type: PropTypes.string,
    inputMode: PropTypes.string,
    pattern: PropTypes.string,
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onBlur: PropTypes.func,
    formActions: PropTypes.object,
    displayErrors: PropTypes.bool,
    onFocus: PropTypes.func,
    noFocusFromLabel: PropTypes.bool,
    autoCompleteField: PropTypes.bool,
    autoCompletePassword: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errorClass: PropTypes.string,
    userProfile: PropTypes.object,
    defaultValue: PropTypes.string,
    messagesError: PropTypes.object,
    style: PropTypes.object,
    label: PropTypes.string,
    inputClass: PropTypes.string,
    minLength: PropTypes.string,
    min: PropTypes.string,
    max: PropTypes.string,
    disabled: PropTypes.bool,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    pasteDisabled: PropTypes.bool,
    onPaste: PropTypes.func,
    tooltip: PropTypes.object,
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    handleOnClick: PropTypes.func,
    prefixSelection: PropTypes.bool,
    handleSave: PropTypes.func,
    isValidated: PropTypes.func,
    isNotValidated: PropTypes.func,
    onlyDigits: PropTypes.bool,
    isPassword: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    innerRef: PropTypes.func,
    isLoading: PropTypes.bool,
    eventCategoryTag: PropTypes.string,
    uppercaseText: PropTypes.bool,
};
